/**
 * Value input for `set` actions: MultiSelectWithTag with async search (same UI/behavior
 * as QueryBuilder condition value fields).
 */

import type { QueryBuilderConfig } from "../../../data-model/queryBuilder";
import MultiSelectWithTag from "../../multiSelectWithTag/MultiSelectWithTag";
import ActionDateValueInput from "./ActionDateValueInput";
import { isActionFieldTimestamp, toValueString } from "./ruleFormConstants";
import { useActionValueSearch } from "./useActionValueSearch";

interface ActionSetValueInputProps {
  /** Stable id prefix for the control (e.g. `action-value-0`). */
  id: string;
  /** Column label shown on the first action row only. */
  label: string;
  conditionConfig: QueryBuilderConfig;
  selectedField: string;
  value: string | string[];
  onValueChange: (value: string) => void;
  placeholder?: string;
}

/**
 * Single-select MultiSelectWithTag for a `set` action value; searches BigQuery when
 * the field has no top_values or the desired value is not in the initial list.
 */
const ActionSetValueInput = ({
  id,
  label,
  conditionConfig,
  selectedField,
  value,
  onValueChange,
  placeholder = "Select value...",
}: ActionSetValueInputProps) => {
  const valueStr = toValueString(value);
  const searchProps = useActionValueSearch(
    conditionConfig,
    selectedField,
    value,
  );

  if (isActionFieldTimestamp(conditionConfig, selectedField)) {
    return (
      <ActionDateValueInput
        id={id}
        label={label}
        enabled={Boolean(selectedField)}
        value={value}
        onValueChange={onValueChange}
      />
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <label
          htmlFor={id}
          className="text-tiny font-medium text-primaryGray-3"
        >
          {label}
        </label>
      ) : null}
      <div
        className={
          selectedField ? "w-full" : "pointer-events-none w-full opacity-50"
        }
      >
        <MultiSelectWithTag
          dropdownWidth="5rem"
          className="w-full"
          selectionMode="single"
          {...searchProps}
          value={valueStr ? [valueStr] : []}
          onChange={(ids) => onValueChange(ids[0] ?? "")}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
};

export default ActionSetValueInput;
